import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from './AuthContext';
import {
  registerServiceWorker,
  getOrCreatePushSubscription,
  getPushSupportStatus,
  setVapidPublicKey,
  hasPushSubscriptions,
  runExclusivePushOperation,
  PUSH_ENABLE_TOAST_ID,
  PUSH_LOADING_MAX_MS
} from '../utils/pushService';

const arrayBufferToBase64 = (buffer) => {
  if (!buffer) return null;
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const NotificationContext = createContext({
  notifications: [],
  unreadCount: 0,
  loading: false,
  preferences: null,
  refresh: () => {},
  markAsRead: async () => {},
  markAllAsRead: async () => {},
  deleteNotification: async () => {},
  openNotificationPanel: () => {},
  ensurePushSubscription: async () => ({ success: false }),
  syncPushSubscriptionIfGranted: async () => ({ success: false }),
  pushSupport: { supported: false, reason: 'unknown' },
  hasServerPushSubscription: false
});

export const NotificationProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [pushSupport, setPushSupport] = useState({ supported: false, reason: 'unknown' });
  const [vapidReady, setVapidReady] = useState(false);
  const initialLoadRef = useRef(true);
  const lastSeenIdRef = useRef(null);
  const audioCtxRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const pollingRef = useRef(null);
  const serviceWorkerReadyRef = useRef(false);
  const metaLoadedRef = useRef(false);
  const notificationFetchInProgressRef = useRef(false);

  // localStorage'dan son görülen bildirim ID'sini yükle
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        const stored = localStorage.getItem('jansoft-last-seen-notification-id');
        if (stored) {
          lastSeenIdRef.current = parseInt(stored, 10);
        }
      }
    } catch (error) {
      // Sessizce atlanıyor
    }
  }, []);

  const calculateUnread = useCallback((items = []) => {
    return items.filter((item) => !item.isRead).length;
  }, []);

  const fetchNotifications = useCallback(async (options = {}) => {
    const includeMeta = options?.includeMeta === true;
    const silent = options?.silent === true;
    if (!user || authLoading) return;
    
    const token = localStorage.getItem('jansoft-token') || sessionStorage.getItem('jansoft-token');
    if (!token) return;
    
    if (notificationFetchInProgressRef.current) {
      return;
    }

    notificationFetchInProgressRef.current = true;
    try {
      if (!silent) {
        setLoading(true);
      }
      
      let notificationRes = { data: { data: { items: [] } } };
      const shouldLoadMeta = includeMeta || !metaLoadedRef.current;
      let prefRes = null;
      let vapidRes = null;
      
      try {
        notificationRes = await api.get('/api/notifications?limit=20');
      } catch (err) {
        if (err?.response?.status !== 401) {
          // 401 dışındaki hatalar sessizce atlanıyor
        }
      }
      
      if (shouldLoadMeta) {
        try {
          prefRes = await api.get('/api/notifications/preferences');
        } catch (err) {
          if (err?.response?.status !== 401) {
            // 401 dışındaki hatalar sessizce atlanıyor
          }
        }

        try {
          vapidRes = await api.get('/api/notifications/vapid-key');
        } catch (err) {
          // VAPID key hatası önemli değil
        }
      }
      
      const items = notificationRes.data?.data?.items || [];
      setNotifications(items);
      setUnreadCount(calculateUnread(items));
      if (shouldLoadMeta) {
        setPreferences(prefRes?.data?.data || null);
        const key = vapidRes?.data?.data?.publicKey || vapidRes?.data?.publicKey || process.env.REACT_APP_WEB_PUSH_PUBLIC_KEY || '';
        setVapidPublicKey(key);
        const supportStatus = getPushSupportStatus();
        setPushSupport(supportStatus);
        setVapidReady(!!key);
        metaLoadedRef.current = true;
      }
      handleIncomingNotifications(items);
    } catch (error) {
      // Sessizce atlanıyor
    } finally {
      notificationFetchInProgressRef.current = false;
      if (!silent) {
        setLoading(false);
      }
    }
  }, [user, authLoading, calculateUnread]);

  const markAsRead = useCallback(
    async (notificationId) => {
      try {
        await api.post('/api/notifications/mark-read', { notificationId });
        setNotifications((prev) =>
          prev.map((item) =>
            item.id === notificationId ? { ...item, isRead: true, readAt: new Date().toISOString() } : item
          )
        );
        setUnreadCount((prev) => Math.max(prev - 1, 0));
      } catch (error) {
        toast.error('Bildirim güncellenemedi');
      }
    },
    []
  );

  const markAllAsRead = useCallback(async () => {
    try {
      await api.post('/api/notifications/mark-all-read');
      setNotifications((prev) => prev.map((item) => ({ ...item, isRead: true, readAt: item.readAt || new Date().toISOString() })));
      setUnreadCount(0);
    } catch (error) {
      toast.error('Bildirimler güncellenemedi');
    }
  }, []);

  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await api.delete(`/api/notifications/${notificationId}`);
      setNotifications((prev) => {
        const deleted = prev.find((item) => item.id === notificationId);
        if (deleted && !deleted.isRead) {
          setUnreadCount((count) => Math.max(count - 1, 0));
        }
        return prev.filter((item) => item.id !== notificationId);
      });
    } catch (error) {
      toast.error('Bildirim silinemedi');
    }
  }, []);

  const openNotificationPanel = useCallback(() => {
    window.dispatchEvent(new CustomEvent('openNotificationPanel'));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return undefined;

    const unlockAudio = () => {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new AudioContextClass();
      }
      if (audioCtxRef.current?.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => {});
      }
      audioUnlockedRef.current = true;
    };

    window.addEventListener('pointerdown', unlockAudio, { passive: true });
    window.addEventListener('keydown', unlockAudio, { passive: true });

    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  const playSound = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      if (!audioUnlockedRef.current) return;
      if (!audioCtxRef.current) {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        audioCtxRef.current = new AudioContext();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
        return;
      }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880;
      osc.connect(gain);
      gain.connect(ctx.destination);
      const now = ctx.currentTime;
      gain.gain.setValueAtTime(0.001, now);
      gain.gain.exponentialRampToValueAtTime(0.2, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
      osc.start(now);
      osc.stop(now + 0.4);
    } catch (error) {
      // Sessizce atlanıyor
    }
  }, []);

  const showToastForNotification = useCallback((notification) => {
    if (!notification || !notification.title) return;
    try {
      playSound();
    } catch (error) {
      // Sessizce atlanıyor
    }
    try {
      toast.custom(
        (t) => {
          if (!t || !t.id) return null;
          const handleClose = (e) => {
            try {
              e?.stopPropagation();
              toast.dismiss(t.id);
            } catch (error) {
              // Sessizce atlanıyor
            }
          };
          const handleClick = () => {
            try {
              toast.dismiss(t.id);
              // Müşteri siparişi bildirimleri için yönlendirme
              if (notification.type === 'customer_order' && notification.metadata?.redirectUrl) {
                window.location.href = notification.metadata.redirectUrl;
              } else {
                openNotificationPanel();
              }
            } catch (error) {
              // Sessizce atlanıyor
            }
          };
          return (
            <div className="pointer-events-auto max-w-sm w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-3 relative">
              <button
                onClick={handleClose}
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition z-10"
                aria-label="Kapat"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div
                onClick={handleClick}
                className="cursor-pointer pr-6"
              >
                <p className="text-xs text-gray-400 dark:text-gray-500">Yeni bildirim</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1">{notification.title || ''}</p>
                <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 whitespace-pre-line">
                  {notification.body || ''}
                </p>
              </div>
            </div>
          );
        },
        { duration: 5000 }
      );
    } catch (error) {
      // Sessizce atlanıyor
    }
  }, [playSound, openNotificationPanel]);

  const handleIncomingNotifications = useCallback(
    (items) => {
      if (!items.length) return;
      const newestId = Math.max(...items.map((item) => item.id));
      if (initialLoadRef.current) {
        initialLoadRef.current = false;
        lastSeenIdRef.current = newestId;
        if (typeof window !== 'undefined') {
          localStorage.setItem('jansoft-last-seen-notification-id', newestId.toString());
        }
        return;
      }
      const lastSeen = lastSeenIdRef.current || 0;
      const newOnes = items.filter((item) => item.id > lastSeen);
      if (newOnes.length) {
        lastSeenIdRef.current = newestId;
        if (typeof window !== 'undefined') {
          localStorage.setItem('jansoft-last-seen-notification-id', newestId.toString());
        }
        newOnes.reverse().forEach(showToastForNotification);
      }
    },
    [showToastForNotification]
  );

  const ensurePushSubscription = useCallback(async (options = {}) => {
    const silent = options.silent === true;

    // Sessiz sync: sadece granted; toast yok; izin diyaloğu yok
    if (silent) {
      if (typeof window === 'undefined' || !('Notification' in window) || Notification.permission !== 'granted') {
        return { success: false, skipped: true };
      }
    }

    return runExclusivePushOperation(async () => {
      const showError = (message) => {
        if (silent) return;
        toast.error(message, { id: PUSH_ENABLE_TOAST_ID, duration: 4000 });
      };
      const showSuccess = (message) => {
        if (silent) return;
        toast.success(message, { id: PUSH_ENABLE_TOAST_ID, duration: 3000 });
      };

      try {
        if (!silent) {
          toast.loading('Push bildirimleri etkinleştiriliyor...', {
            id: PUSH_ENABLE_TOAST_ID,
            duration: PUSH_LOADING_MAX_MS
          });
        }

        const supportStatus = typeof window !== 'undefined' ? getPushSupportStatus() : { supported: false };
        setPushSupport(supportStatus);

        if (!supportStatus?.supported) {
          const reason = supportStatus?.reason || 'unknown';
          const messages = {
            iosStandalone: 'iOS\'ta bildirim için Safari\'den "Paylaş > Ana Ekrana Ekle" ile uygulamayı yükleyin.',
            missingKey: 'Push anahtarları sunucuda tanımlı değil. Yöneticiye haber verin.',
            serviceWorker: 'Tarayıcınız service worker desteklemiyor. Chrome veya Edge kullanmayı deneyin.',
            notification: 'Tarayıcınız bildirim API\'sini desteklemiyor.'
          };
          showError(messages[reason] || 'Push bildirimleri bu cihazda etkinleştirilemiyor.');
          return { success: false };
        }

        if (!vapidReady) {
          showError('Push anahtarları hazır değil; sayfayı yenileyip tekrar deneyin.');
          return { success: false };
        }

        if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'denied') {
          const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
          showError(
            isMobile
              ? 'Bildirim izinleri engellenmiş. Telefon ayarlarından izin verin.'
              : 'Bildirim izinleri engellenmiş. Tarayıcı ayarlarından izin verin.'
          );
          return { success: false };
        }

        // register → ready → active (iOS race önleme)
        let registration;
        try {
          registration = await registerServiceWorker();
          serviceWorkerReadyRef.current = true;
        } catch (swError) {
          showError(`Service Worker hatası: ${swError.message || 'Bilinmeyen hata'}`);
          return { success: false, error: swError };
        }

        if (!registration?.pushManager) {
          showError('Push Manager bu tarayıcıda desteklenmiyor.');
          return { success: false };
        }

        if (
          !silent &&
          typeof window !== 'undefined' &&
          'Notification' in window &&
          Notification.permission === 'default'
        ) {
          toast.loading('Bildirim izni isteniyor...', {
            id: PUSH_ENABLE_TOAST_ID,
            duration: 4000
          });
        }

        let subscription;
        try {
          subscription = await getOrCreatePushSubscription(registration);
        } catch (subError) {
          showError(subError?.message || 'Push aboneliği oluşturulamadı');
          return { success: false, error: subError };
        }

        if (!subscription?.endpoint) {
          showError('Push aboneliği oluşturulamadı.');
          return { success: false };
        }

        const p256dhKey = subscription.getKey('p256dh');
        const authKey = subscription.getKey('auth');
        if (!p256dhKey || !authKey) {
          showError('Subscription keys bulunamadı.');
          return { success: false };
        }

        const p256dhBase64 = arrayBufferToBase64(p256dhKey);
        const authBase64 = arrayBufferToBase64(authKey);
        const userAgent = navigator.userAgent;
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
        const browser = userAgent.includes('Edg')
          ? 'edge'
          : userAgent.includes('Chrome')
            ? 'chrome'
            : userAgent.includes('Firefox')
              ? 'firefox'
              : userAgent.includes('Safari')
                ? 'safari'
                : 'unknown';

        const subscriptionData = {
          endpoint: subscription.endpoint,
          keys: { p256dh: p256dhBase64, auth: authBase64 },
          metadata: {
            userAgent,
            browser,
            deviceType: isMobile ? 'mobile' : 'desktop',
            timestamp: new Date().toISOString()
          }
        };

        try {
          const saveRes = await api.post('/api/notifications/push-subscription', subscriptionData);
          if (saveRes?.data?.data) {
            setPreferences(saveRes.data.data);
          }
        } catch (apiError) {
          showError(
            `Push aboneliği kaydedilemedi: ${apiError?.response?.data?.message || apiError?.message || 'Bilinmeyen hata'}`
          );
          return { success: false, error: apiError };
        }

        showSuccess(`Push bildirimleri ${isMobile ? 'mobil cihazda' : 'web tarayıcıda'} etkinleştirildi`);
        fetchNotifications({ includeMeta: true, silent: true });
        return { success: true };
      } catch (error) {
        showError(error?.message || 'Push bildirimi etkinleştirilemedi');
        return { success: false, error };
      }
    });
  }, [fetchNotifications, vapidReady]);

  const syncPushSubscriptionIfGranted = useCallback(async () => {
    return ensurePushSubscription({ silent: true });
  }, [ensurePushSubscription]);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setPreferences(null);
      metaLoadedRef.current = false;
      initialLoadRef.current = true;
      lastSeenIdRef.current = null;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }
    
    const token = localStorage.getItem('jansoft-token') || sessionStorage.getItem('jansoft-token');
    if (!token) return;
    
    const delay = initialLoadRef.current ? 200 : 100;
    const timeoutId = setTimeout(() => {
      const currentToken = localStorage.getItem('jansoft-token') || sessionStorage.getItem('jansoft-token');
      if (!currentToken) return;
      
        fetchNotifications({ includeMeta: true });
      initialLoadRef.current = false;
      
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      pollingRef.current = setInterval(() => {
        const checkToken = localStorage.getItem('jansoft-token') || sessionStorage.getItem('jansoft-token');
        if (checkToken && user) {
          fetchNotifications({ silent: true });
        }
      }, 30000);
    }, delay);
    
    return () => {
      clearTimeout(timeoutId);
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [user, authLoading, fetchNotifications]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event) => {
      if (event.data?.type === 'NEW_NOTIFICATION') {
        fetchNotifications({ silent: true });
      }
      if (event.data?.type === 'OPEN_URL' && event.data?.url) {
        window.location.href = event.data.url;
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handler);
    };
  }, [fetchNotifications]);

  useEffect(() => {
    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        fetchNotifications({ silent: true });
        syncPushSubscriptionIfGranted();
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);
    return () => document.removeEventListener('visibilitychange', visibilityHandler);
  }, [fetchNotifications, syncPushSubscriptionIfGranted]);

  useEffect(() => {
    if (vapidReady) {
      const newStatus = getPushSupportStatus();
      setPushSupport(newStatus);
    }
  }, [vapidReady]);

  // Login / app open: izin daha önce verilmişse aboneliği backend'e yeniden kaydet
  useEffect(() => {
    if (authLoading || !user || !vapidReady) return;
    const token = localStorage.getItem('jansoft-token') || sessionStorage.getItem('jansoft-token');
    if (!token) return;
    const timer = setTimeout(() => {
      syncPushSubscriptionIfGranted();
    }, 400);
    return () => clearTimeout(timer);
  }, [user, authLoading, vapidReady, syncPushSubscriptionIfGranted]);

  const hasServerPushSubscription = hasPushSubscriptions(preferences?.pushSubscription);

  const value = {
    notifications,
    unreadCount,
    loading,
    preferences,
    refresh: () => fetchNotifications({ includeMeta: true }),
    markAsRead,
    markAllAsRead,
    deleteNotification,
    openNotificationPanel,
    ensurePushSubscription,
    syncPushSubscriptionIfGranted,
    pushSupport,
    hasServerPushSubscription
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const useNotifications = () => useContext(NotificationContext);


